import { useState, useEffect } from 'react'
import { 
    VStack, Box, Text, Input, Button, Table, 
    Thead, Tbody, Tr, Th, Td, IconButton, 
    HStack, useToast, Divider, Grid, GridItem,
    List, ListItem
  } from '@chakra-ui/react'
  import { 
    RepeatIcon, 
    DeleteIcon, 
    AddIcon,
    CloseIcon 
  } from '@chakra-ui/icons'

interface EnvVariable {
  key: string
  value: string
}

interface Model {
  id: string
  name: string
  repo: string
  status: 'available' | 'deployed'
  config?: {
    runCommand: string
    envVariables: EnvVariable[]
  }
}

export const ModelConfig = () => {
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null)
  const [runCommand, setRunCommand] = useState('python -m src.main')
  const [envVariables, setEnvVariables] = useState<EnvVariable[]>([])
  const [isDeploying, setIsDeploying] = useState<boolean>(false)
  const [isUpdating, setIsUpdating] = useState<boolean>(false)
  const [newEnvKey, setNewEnvKey] = useState('')
  const [newEnvValue, setNewEnvValue] = useState('')
  const [models, setModels] = useState<Model[]>([])
  const toast = useToast()

  const selectedModel = models.find(m => m.id === selectedModelId)

  const handleUpdateCode = async () => {
    setIsUpdating(true)
    try {
      // TODO: Implement GitHub webhook or API call to pull latest code
      await new Promise(resolve => setTimeout(resolve, 1000))
      toast({
        title: 'Code updated successfully',
        status: 'success',
        duration: 3000,
      })
    } catch (error: any) {
      toast({
        title: 'Failed to update code',
        status: 'error',
        duration: 3000,
      })
    } finally {
      setIsUpdating(false)
    }
  }

  const handleDeleteEnvVariable = (index: number) => {
    setEnvVariables(prev => prev.filter((_, i) => i !== index))
  }

  const handleAddEnvVariable = () => {
    if (newEnvKey && newEnvValue) {
      setEnvVariables(prev => [...prev, { key: newEnvKey, value: newEnvValue }])
      setNewEnvKey('')
      setNewEnvValue('')
    }
  }

  // Load existing models and check for new ones
  useEffect(() => {
    // First load existing models from localStorage
    const existingModels = localStorage.getItem('models')
    const parsedModels = existingModels ? JSON.parse(existingModels) : []
    setModels(parsedModels)

    // Then check for newly added model
    const newRepo = localStorage.getItem('selectedRepo')
    if (newRepo) {
      console.log('Found new repo in localStorage:', newRepo)
      const newModel = {
        id: Date.now().toString(),
        name: newRepo.split('/').pop() || 'New Model',
        repo: newRepo,
        status: 'available' as const
      }
      
      setModels(prev => {
        const updatedModels = prev.some(model => model.repo === newRepo && model.status === 'available')
          ? prev
          : [...prev, newModel]
        // Save to localStorage
        localStorage.setItem('models', JSON.stringify(updatedModels))
        return updatedModels
      })
      
      setSelectedModelId(newModel.id)
      localStorage.removeItem('selectedRepo')
    }
  }, [])

  // Update localStorage whenever models change
  useEffect(() => {
    localStorage.setItem('models', JSON.stringify(models))
  }, [models])

  // Update config when selecting different model
  useEffect(() => {
    if (selectedModelId) {
      const model = models.find(m => m.id === selectedModelId)
      if (model?.config) {
        setRunCommand(model.config.runCommand)
        setEnvVariables(model.config.envVariables)
      } else {
        // Reset to defaults if no config
        setRunCommand('python -m src.main')
        setEnvVariables([])
      }
    }
  }, [selectedModelId])

  const handleRemoveModel = (id: string, status: 'available' | 'deployed') => {
    const model = models.find(m => m.id === id)
    if (!model) return

    if (status === 'deployed') {
      // TODO: Call backend to undeploy model
      toast({
        title: 'Model Undeployed',
        description: `${model.name} has been undeployed`,
        status: 'info',
      })
    } else {
      // Remove GitHub connection for available model
      toast({
        title: 'Model Removed',
        description: `${model.name} has been removed from available models`,
        status: 'info',
      })
    }
    
    setModels(models.filter(m => m.id !== id))
    if (selectedModelId === id) {
      setSelectedModelId(null)
    }
  }

  const handleDeploy = async () => {
    if (!selectedModelId) return
    
    setIsDeploying(true)
    try {
      const selectedModel = models.find((m: Model) => m.id === selectedModelId)
      if (!selectedModel) throw new Error('Model not found')

      // Create deployed instance with current config
      const deployedModel = {
        id: Date.now().toString(),
        name: selectedModel.name,
        repo: selectedModel.repo,
        status: 'deployed' as const,
        config: {
          runCommand,
          envVariables
        }
      }

      // TODO: Call backend to deploy model
      await new Promise(resolve => setTimeout(resolve, 2000))

      setModels(prev => [...prev, deployedModel])
      
      toast({
        title: 'Model Deployed Successfully',
        description: `${deployedModel.name} has been deployed`,
        status: 'success',
      })
    } catch (error: any) {
      toast({
        title: 'Deployment Failed',
        description: error?.message || 'Unknown error occurred',
        status: 'error',
      })
    } finally {
      setIsDeploying(false)
    }
  }

  const ModelList = ({ status }: { status: 'available' | 'deployed' }) => (
    <List spacing={2}>
      {models
        .filter(model => model.status === status)
        .map(model => (
          <ListItem 
            key={model.id}
            p={2}
            bg={selectedModelId === model.id ? "blue.50" : "gray.50"}
            borderRadius="md"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            cursor="pointer"
            onClick={() => setSelectedModelId(model.id)}
          >
            <Text fontSize="sm">{model.name}</Text>
            <IconButton
              aria-label="Remove model"
              icon={<CloseIcon />}
              size="xs"
              colorScheme="red"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation()
                handleRemoveModel(model.id, status)
              }}
            />
          </ListItem>
        ))}
    </List>
  )

  return (
    <Grid templateColumns="250px 1fr" gap={6} width="100%" p={4}>
      <GridItem>
        <VStack align="stretch" spacing={4}>
          <Box>
            <Text fontWeight="bold" mb={2}>Available Models</Text>
            <ModelList status="available" />
          </Box>
          
          <Divider />
          
          <Box>
            <Text fontWeight="bold" mb={2}>Deployed Models</Text>
            <ModelList status="deployed" />
          </Box>
        </VStack>
      </GridItem>

      <GridItem>
        {selectedModelId ? (
          <VStack spacing={6} align="start" width="100%" maxW="800px">
            <Box width="100%">
              <HStack justify="space-between" width="100%">
                <Text fontSize="lg" fontWeight="bold">
                  Repository: {models.find(m => m.id === selectedModelId)?.repo}
                </Text>
                <Button
                  leftIcon={<RepeatIcon />}
                  colorScheme="blue"
                  size="sm"
                  onClick={handleUpdateCode}
                  isLoading={isUpdating}
                >
                  Update Code
                </Button>
              </HStack>
            </Box>

            <Box width="100%">
              <Text mb={2} fontWeight="bold">Run Command</Text>
              <Input
                value={runCommand}
                onChange={(e) => setRunCommand(e.target.value)}
                placeholder="Enter run command"
                width="100%"
              />
            </Box>

            <Box width="100%">
              <Text mb={2} fontWeight="bold">Environment Variables</Text>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Key</Th>
                    <Th>Value</Th>
                    <Th width="50px">Action</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {envVariables.map((env, index) => (
                    <Tr key={index}>
                      <Td>{env.key}</Td>
                      <Td>{env.value}</Td>
                      <Td>
                        <IconButton
                          aria-label="Delete variable"
                          icon={<DeleteIcon />}
                          size="sm"
                          colorScheme="red"
                          onClick={() => handleDeleteEnvVariable(index)}
                        />
                      </Td>
                    </Tr>
                  ))}
                  <Tr>
                    <Td>
                      <Input
                        placeholder="KEY"
                        value={newEnvKey}
                        onChange={(e) => setNewEnvKey(e.target.value)}
                        size="sm"
                      />
                    </Td>
                    <Td>
                      <Input
                        placeholder="VALUE"
                        value={newEnvValue}
                        onChange={(e) => setNewEnvValue(e.target.value)}
                        size="sm"
                      />
                    </Td>
                    <Td>
                      <IconButton
                        aria-label="Add variable"
                        icon={<AddIcon />}
                        size="sm"
                        colorScheme="green"
                        onClick={handleAddEnvVariable}
                      />
                    </Td>
                  </Tr>
                </Tbody>
              </Table>
            </Box>

            <Divider my={4} />
            
            <Box width="100%">
              <Button
                colorScheme="green"
                size="md"
                width="100%"
                onClick={handleDeploy}
                isLoading={isDeploying}
                loadingText={`${selectedModel?.status === 'deployed' ? 'Redeploying' : 'Deploying'} Model...`}
              >
                {selectedModel?.status === 'deployed' ? 'Redeploy Model' : 'Deploy Model'}
              </Button>
            </Box>
          </VStack>
        ) : (
          <Text>Select an available model to configure and deploy.</Text>
        )}
      </GridItem>
    </Grid>
  )
}
